import { Capacitor } from '@capacitor/core';
import { f7 } from 'framework7-react';
import { getT } from '@/i18n';

// Mobile-phone layout breakpoint - mirrors App.tsx's BREAKPOINT.
const MOBILE_BREAKPOINT = 768;

// Phone layout OR any native (Capacitor) build - incl. the wide operator tablets.
function isMobileSurface(): boolean {
  return Capacitor.isNativePlatform() || window.innerWidth < MOBILE_BREAKPOINT;
}

// ── Success vs error classification ──────────────────────────────────────────
// On mobile/tablet, routine SUCCESS confirmations (save, toggle, status change,
// create/update/delete, copy, …) show NOTHING; errors/validation become an
// OK-dialog. We can only see the toast's resolved `text`, so we classify by the
// i18n KEY that produced it: confirmation keys use past-tense/confirmation verbs
// (saved, updated, deleted, issued, resumed, …) while error keys use a distinct
// vocabulary (failed, invalid, required, couldNot, enter, pick, …). Matching the
// PAST-TENSE forms is deliberate - it avoids catching error keys built on the
// present tense (saveFailed, cantDelete, couldNotSave). Anything unrecognised
// (incl. dynamic `e.message` errors) falls through to a dialog, so a real
// failure is never silently swallowed.

// If a key contains any of these it is an ERROR/validation toast → never suppress.
const ERROR_TOKENS = [
  'error',
  'failed',
  'fail',
  'invalid',
  'required',
  'couldnot',
  'cant',
  'cannot',
  'missing',
  'denied',
  'mustbe',
  'atleast',
  'pick',
  'enter',
  'wrong',
  'notfound',
  'forbidden',
  'unauthorized',
  'tapdishes',
  'nofree',
  'noother',
  'overnight',
  'without',
  'idformat',
  'reconnect',
  'outofrange',
  'fixerror',
  'checktimes',
];

// Otherwise, if a key contains any of these it is a SUCCESS confirmation → suppress.
const SUCCESS_TOKENS = [
  'saved',
  'updated',
  'added',
  'deleted',
  'removed',
  'created',
  'archived',
  'issued',
  'sent',
  'resumed',
  'paused',
  'toggled',
  'queued',
  'adjusted',
  'linked',
  'attached',
  'voided',
  'restored',
  'moved',
  'merged',
  'closed',
  'opened',
  'collected',
  'recorded',
  'seated',
  'fired',
  'accepted',
  'reopened',
  'copied',
  'applied',
  'published',
  'turned',
  'shown',
  'hidden',
  'renamed',
  'marked',
  'done',
  'success',
];

// Confirmation keys whose names carry none of the tokens above.
const STRAGGLER_KEYS = new Set([
  'nowFollowingGeneral',
  'capacityModeColon',
  'tableModeColon',
  'tableOrderingOn',
  'tableOrderingOff',
  'bookingsAutoConfirmedToast',
  'bookingsNeedReview',
  'minSet',
  'paymentWindowSet',
]);

function isSuccessKey(key: string): boolean {
  if (STRAGGLER_KEYS.has(key)) return true;
  const k = key.toLowerCase();
  if (ERROR_TOKENS.some((t) => k.includes(t))) return false;
  return SUCCESS_TOKENS.some((t) => k.includes(t));
}

// A few success toasts pass hardcoded English strings (no i18n key) - match those
// directly. Templated ones (`Gift card X issued`, `X removed`, `Switched to X`)
// are matched by shape.
function isHardcodedSuccessText(text: string): boolean {
  const exact = new Set([
    'Balance adjusted',
    'Points adjusted',
    'Loyalty settings saved',
    'Set as primary',
    'Invitation sent',
  ]);
  if (exact.has(text)) return true;
  if (text.startsWith('Switched to ')) return true;
  if (text.endsWith(' issued')) return true;
  if (text.endsWith(' removed')) return true;
  return false;
}

const successCache = new WeakMap<object, Set<string>>();

// Collect every string leaf under `node` (used for reservation status subtrees
// whose key names carry no success token: confirmed/rejected/cancelled/arrived/…).
function addStringLeaves(node: unknown, set: Set<string>): void {
  if (!node || typeof node !== 'object') return;
  for (const v of Object.values(node as Record<string, unknown>)) {
    if (typeof v === 'string') set.add(v);
    else if (v && typeof v === 'object') addStringLeaves(v, set);
  }
}

// Walk the whole tree, adding string leaves whose KEY is a success confirmation.
function addSuccessKeyed(node: unknown, set: Set<string>): void {
  if (!node || typeof node !== 'object') return;
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    if (typeof v === 'string') {
      if (isSuccessKey(k)) set.add(v);
    } else if (v && typeof v === 'object') {
      addSuccessKeyed(v, set);
    }
  }
}

function successTexts(): Set<string> {
  const t = getT() as Record<string, unknown>;
  const cached = successCache.get(t);
  if (cached) return cached;

  const set = new Set<string>();
  if (typeof t.saved === 'string') set.add(t.saved);
  const reservations = t.reservations as Record<string, unknown> | undefined;
  addStringLeaves(reservations?.toast, set); // confirmed/rejected/cancelled/arrived/…
  addStringLeaves(reservations?.displayStatus, set); // waiting/rescheduled
  addSuccessKeyed(t, set); // all save/update/delete/… confirmations, tree-wide

  successCache.set(t, set);
  return set;
}

let installed = false;

/**
 * On the mobile/tablet surface, route `f7.toast.show()` away from a fleeting
 * toast: SUCCESS confirmations are suppressed entirely; everything else (errors,
 * validation) becomes a dismiss-on-OK dialog. Desktop keeps toasts. Installed
 * ONCE, after Framework7 has initialised; covers all ~300 call sites untouched.
 */
export function installMobileToastAsDialog(): void {
  if (installed) return;
  installed = true;

  const originalShow = f7.toast.show.bind(f7.toast);

  f7.toast.show = ((params: Parameters<typeof originalShow>[0]) => {
    const text = params?.text;
    if (isMobileSurface() && typeof text === 'string' && text.length > 0) {
      if (successTexts().has(text) || isHardcodedSuccessText(text)) {
        return toastStub<ReturnType<typeof originalShow>>(); // routine success → silent
      }
      f7.dialog
        .create({
          text,
          buttons: [{ text: 'OK', close: true, cssClass: 'dialog-btn-primary' }],
        })
        .open();
      return toastStub<ReturnType<typeof originalShow>>();
    }
    return originalShow(params);
  }) as typeof f7.toast.show;
}

// Minimal Toast-like stub so callers chaining .open()/.close() don't throw.
function toastStub<T>(): T {
  return {
    open() {
      return this;
    },
    close() {
      return this;
    },
    destroy() {
      return this;
    },
  } as unknown as T;
}
